/**
 * Service formula / content helpers (KAN-157 god-component split).
 *
 * Moved out of ServiceManagementPage.tsx: pure, hook-free functions and types
 * that read a Service/Chain and produce display-ready data (formula lines,
 * client-facing input lines, pricing positions, questionnaire fields, type
 * labels/colors). No JSX here — presentational components in
 * `@/components/services/detail/` consume these.
 */
import { BookOpen, Calculator, GitBranch, PencilLine, Tag, type LucideIcon } from 'lucide-react';
import type { Chain, ChainNode, PricingItem, QuestionnaireField, Service } from '@/services/api/services';

// ── Formula extraction (shared by CalculatorDetails & ModifierDetails) ─────
export type FormulaLine = { key: string; out: string | undefined; formula: string };

// Walk a chain's nodes in execution order (falling back to object key order),
// skipping nodes without a formula and trivial pass-through nodes whose formula
// is just a bare identifier that re-exposes an input. Emits readable
// `out = formula` lines. Shared between the calculator body and the «Постобработка»
// modifier overlay (TASK-189) so both render formulas identically.
export function extractFormulaLines(chain: Chain): FormulaLine[] {
  const nodes = chain.nodes_data || {};
  const order =
    chain.execution_order && chain.execution_order.length > 0 ? chain.execution_order : Object.keys(nodes);
  return order
    .map((nodeName): FormulaLine | null => {
      const node: ChainNode | undefined = nodes[nodeName];
      if (!node || !node.formula) return null;
      const out = (node.output_variable as string | undefined) ?? (node.output as string | undefined);
      const formula = node.formula.trim();
      const isBareIdentifier = /^[A-Za-z_$][\w$]*$/.test(formula);
      if (isBareIdentifier) return null;
      return { key: nodeName, out, formula };
    })
    .filter((line): line is FormulaLine => line !== null);
}

// ── Client-input helpers (TASK-197 «Входные параметры») ───────────────────
//
// Service-internal inputs are NOT client-facing and must be excluded from the
// «Входные параметры» section: results piped from children (*_from_child),
// other services' results (результат_*), the implicit Σ of pricelist positions
// (базовая_сумма) and the classifier selection key (способ).
export function isServiceInput(name: string): boolean {
  const n = name.trim().toLowerCase();
  return n.endsWith('_from_child') || n.startsWith('результат_') || n === 'базовая_сумма' || n === 'способ';
}

export type InputLine = { key: string; text: string };

// Build readable «name (unit) — type» lines from a chain's required_inputs,
// dropping service-internal inputs.
export function clientInputLines(chain: Chain): InputLine[] {
  const inputs = chain.required_inputs || [];
  return inputs
    .map((inp, idx): InputLine | null => {
      const name = typeof inp === 'string' ? inp : inp.name ?? '';
      const unit = typeof inp === 'string' ? '' : inp.unit ?? '';
      const type = typeof inp === 'string' ? '' : inp.type ?? '';
      if (!name || isServiceInput(name)) return null;
      const suffix = `${unit ? ` (${unit})` : ''}${type ? ` — ${type}` : ''}`;
      return { key: name || String(idx), text: `${name}${suffix}` };
    })
    .filter((line): line is InputLine => line !== null);
}

// ── Manual tree-test leaf inputs (TASK-260) ───────────────────────────────
//
// Raw {name, unit} fields a client must type for a calculator chain (drops
// service-internal inputs). Used by the inline test-mode input rendering.
export type InputField = { name: string; unit: string };

export function clientInputFields(chain: Chain): InputField[] {
  const inputs = chain.required_inputs || [];
  return inputs
    .map((inp): InputField | null => {
      const name = typeof inp === 'string' ? inp : (inp.name ?? '');
      const unit = typeof inp === 'string' ? '' : (inp.unit ?? '');
      if (!name || isServiceInput(name)) return null;
      return { name, unit };
    })
    .filter((f): f is InputField => f !== null);
}

// Read a service's pricing positions as an array (robust to null).
export function pricingItems(service: Service): PricingItem[] {
  return Array.isArray(service.pricing_data) ? service.pricing_data : [];
}

// Output name/unit for the «Выходные данные» section. Uses the LAST chain's
// output_name (fallback to its last node's output_variable/output), plus the
// chain's output_unit. Returns null when there is no usable output info —
// e.g. a classifier with no chain (the section is then hidden entirely).
export function outputInfo(service: Service): string | null {
  const chains = service.chains || [];
  if (chains.length === 0) return null;
  const chain = chains[chains.length - 1];

  let name = chain.output_name?.trim() || '';
  if (!name) {
    // Fall back to the last node's output variable.
    const nodes = chain.nodes_data || {};
    const order =
      chain.execution_order && chain.execution_order.length > 0 ? chain.execution_order : Object.keys(nodes);
    for (let i = order.length - 1; i >= 0; i -= 1) {
      const node: ChainNode | undefined = nodes[order[i]];
      const out = node?.output_variable ?? node?.output;
      if (out && out.trim()) {
        name = out.trim();
        break;
      }
    }
  }
  if (!name) return null;
  const unit = chain.output_unit?.trim();
  return unit ? `${name} (${unit})` : name;
}

// ── Questionnaire details (TASK-332) ──────────────────────────────────────

// Localized labels for questionnaire question types.
export const QUESTION_TYPE_LABELS: Record<string, string> = {
  number: 'Число',
  text: 'Текст',
  enum: 'Выбор',
  document: 'Документ',
};

// Read a service's questionnaire fields as an array (robust to null).
export function questionnaireFields(service: Service): QuestionnaireField[] {
  return Array.isArray(service.questionnaire_fields) ? service.questionnaire_fields : [];
}

// «Входные параметры» lines for a questionnaire — built from
// questionnaire_fields (a questionnaire has NO chain): «name (unit) — type».
export function questionnaireInputLines(service: Service): InputLine[] {
  return questionnaireFields(service).map((field, idx) => {
    const typeLabel = QUESTION_TYPE_LABELS[field.type] || field.type;
    const unit = field.unit ? ` (${field.unit})` : '';
    return {
      key: field.name || String(idx),
      text: `${field.name}${unit} — ${typeLabel}`,
    };
  });
}

// ── Type badges / colors ───────────────────────────────────────────────────
//
// Localized type badges. The badge always reflects the REAL
// calculation_method. `aggregator` is a role (is_aggregator), not a
// calculation method — it is shown via a separate «с подуслугами» marker.
export const METHOD_LABELS: Record<string, string> = {
  calculator: 'Калькулятор',
  classifier: 'Классификатор',
  pricelist: 'Прайс-лист',
  reference: 'Справочник',
  questionnaire: 'Опросник',
};

export const METHOD_VARIANTS: Record<string, 'default' | 'secondary' | 'outline'> = {
  calculator: 'default',
  classifier: 'secondary',
  pricelist: 'secondary',
  reference: 'outline',
  questionnaire: 'secondary',
};

// Color-dot classes for the compact tree view (replaces verbose text badges).
export const METHOD_COLORS: Record<string, string> = {
  calculator: 'bg-blue-500',
  classifier: 'bg-amber-500',
  pricelist: 'bg-emerald-500',
  reference: 'bg-gray-400',
  questionnaire: 'bg-violet-500',
};

// Icon for the type marker badge (KAN-233 round 4 — icons replace letters).
// One lucide-react icon per calculation method, rendered white-on-color
// inside the 14px badge (ServiceTypeMarker) — shared by the compact tree
// node dot and the ServiceTypeLegend swatch so both stay visually in sync.
export const METHOD_ICONS: Record<string, LucideIcon> = {
  calculator: Calculator,
  pricelist: Tag,
  classifier: GitBranch,
  reference: BookOpen,
  questionnaire: PencilLine,
};

// ── Legend for color dots (only shows types present in the tree) ──────────
export const METHOD_ORDER: string[] = [
  'calculator',
  'pricelist',
  'classifier',
  'questionnaire',
  'reference',
];

// Always returns the REAL calculation method label (never «Агрегатор»).
export function serviceTypeLabel(service: Service): string {
  return METHOD_LABELS[service.calculation_method] || service.calculation_method;
}

// Variant for the real calculation method (no is_aggregator branch).
export function serviceTypeVariant(service: Service): 'default' | 'secondary' | 'outline' {
  return METHOD_VARIANTS[service.calculation_method] || 'default';
}

// Dot color class for the compact tree view.
export function serviceTypeDot(service: Service): string {
  return METHOD_COLORS[service.calculation_method] || 'bg-gray-400';
}
